/**
 * Victory Scene
 * Win state with celebration and stats
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { audioEngine } from '../systems/AudioEngine.js';
import { stateManager } from '../systems/StateManager.js';

export class VictoryScene extends Phaser.Scene {
    constructor() {
        super({ key: 'VictoryScene' });
    }

    init(data) {
        this.finalScore = data.score || 0;
        this.survivalTime = data.survivalTime || 0;
    }

    create() {
        // Brighter background for victory
        this.cameras.main.setBackgroundColor(0x1a1a2e);
        this.cameras.main.fadeIn(500);

        // Victory audio
        audioEngine.playVictory();

        // Create visuals
        this.createBackground();
        this.createTitle();
        this.createStats();
        this.createButtons();
    }

    createBackground() {
        // Particle celebration
        for (let i = 0; i < 50; i++) {
            this.time.delayedCall(i * 100, () => {
                this.createCelebrationParticle();
            });
        }
    }

    createCelebrationParticle() {
        const x = Phaser.Math.Between(0, this.scale.width);
        const colors = [0xFFD700, 0x00FF00, 0x00FFFF, 0xFF00FF];
        const color = colors[Phaser.Math.Between(0, colors.length - 1)];

        const particle = this.add.circle(x, this.scale.height + 50, Phaser.Math.Between(3, 8), color, 0.8);

        this.tweens.add({
            targets: particle,
            y: -50,
            x: x + Phaser.Math.Between(-100, 100),
            alpha: 0,
            duration: Phaser.Math.Between(3000, 5000),
            onComplete: () => particle.destroy()
        });
    }

    createTitle() {
        const centerX = this.scale.width / 2;

        // Victory title
        this.titleText = this.add.text(centerX, 120, 'REGRESSION COMPLETE', {
            fontSize: '42px',
            fontFamily: 'Courier New, monospace',
            color: '#00FF00',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // Subtitle
        this.add.text(centerX, 170, 'You have escaped the Overfitter', {
            fontSize: '18px',
            fontFamily: 'Georgia, serif',
            color: '#88FF88',
            fontStyle: 'italic'
        }).setOrigin(0.5);

        // Glow effect
        this.tweens.add({
            targets: this.titleText,
            alpha: { from: 0.8, to: 1 },
            duration: 1000,
            yoyo: true,
            repeat: -1
        });
    }

    createStats() {
        const centerX = this.scale.width / 2;
        const statsY = this.scale.height / 2 - 20;

        // Final score (time survived in seconds)
        this.add.text(centerX, statsY, `SCORE: ${this.finalScore}`, {
            fontSize: '36px',
            fontFamily: 'Courier New, monospace',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // Check for high score
        const isHighScore = stateManager.updateHighScore(this.finalScore);
        if (isHighScore) {
            this.add.text(centerX, statsY + 45, 'NEW RECORD!', {
                fontSize: '24px',
                fontFamily: 'Arial',
                color: '#FFD700',
                fontStyle: 'bold'
            }).setOrigin(0.5);
        }

        // Survival time formatted
        const minutes = Math.floor(this.survivalTime / 60000);
        const seconds = Math.floor((this.survivalTime % 60000) / 1000);
        this.add.text(centerX, statsY + 90, `Time: ${minutes}:${seconds.toString().padStart(2, '0')}`, {
            fontSize: '20px',
            fontFamily: 'Courier New, monospace',
            color: '#AAAAAA'
        }).setOrigin(0.5);

        // Rating based on score (time in seconds)
        const rating = this.calculateRating();
        this.add.text(centerX, statsY + 130, `Rating: ${rating}`, {
            fontSize: '24px',
            fontFamily: 'Courier New, monospace',
            color: '#FFD700'
        }).setOrigin(0.5);
    }

    calculateRating() {
        // Rating based on score (time survived in seconds)
        // Boss triggers at ~90s, full fight ~90s more = ~180s total for victory
        if (this.finalScore >= 180) return 'S - PERFECT FIT';
        if (this.finalScore >= 150) return 'A - EXCELLENT';
        if (this.finalScore >= 120) return 'B - GOOD';
        if (this.finalScore >= 100) return 'C - ADEQUATE';
        return 'D - BARELY SURVIVED';
    }

    createButtons() {
        const centerX = this.scale.width / 2;
        const buttonY = this.scale.height - 120;

        // Play again button
        this.createButton(centerX - 100, buttonY, 'PLAY AGAIN', () => {
            audioEngine.playClick();
            this.cameras.main.fadeOut(300);
            this.time.delayedCall(300, () => {
                this.scene.start('HoneypotScene');
            });
        });

        // Main menu button
        this.createButton(centerX + 100, buttonY, 'MENU', () => {
            audioEngine.playClick();
            this.cameras.main.fadeOut(300);
            this.time.delayedCall(300, () => {
                this.scene.start('TitleScene');
            });
        });
    }

    createButton(x, y, text, callback) {
        const button = this.add.graphics();
        button.fillStyle(0x006600, 0.9);
        button.fillRoundedRect(x - 80, y - 25, 160, 50, 10);

        const hitArea = this.add.rectangle(x, y, 160, 50, 0x000000, 0)
            .setInteractive({ useHandCursor: true });

        const buttonText = this.add.text(x, y, text, {
            fontSize: '18px',
            fontFamily: 'Courier New, monospace',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        hitArea.on('pointerover', () => {
            button.clear();
            button.fillStyle(0x008800, 0.9);
            button.fillRoundedRect(x - 80, y - 25, 160, 50, 10);
        });

        hitArea.on('pointerout', () => {
            button.clear();
            button.fillStyle(0x006600, 0.9);
            button.fillRoundedRect(x - 80, y - 25, 160, 50, 10);
        });

        hitArea.on('pointerdown', callback);
    }
}
